import { useMemo, useState } from 'react';

import HeaderUno from '../Metodos/Compartidos/HeaderUno.js';
import HeaderDos from '../Metodos/Compartidos/HeaderDos.js';

import { Cuerpo } from '../Css/StyledHeader.js';
import { CITAS_MOCK, ESTADOS_CITA, ESTADOS_PAGO } from '../../constants/calendarConstants.js';
import confirmedIcon from '../../image/confirmed-icon.png';
import cancelledIcon from '../../image/cancelled-icon.png';
import searchIcon from '../../image/buscar.png';

import {
  AssistanceMainContainer,
  AssistanceContent,
  MetricsGrid,
  MetricCard,
  MetricLabel,
  MetricValue,
  FiltersCard,
  FiltersLabel,
  FiltersRow,
  FilterGroup,
  FilterText,
  DateInput,
  SearchWrapper,
  SearchInput,
  IconButton,
  SearchIcon,
  TableCard,
  TableScroll,
  TableHeaderRow,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  AttendanceSelect,
  StatusIconWrapper,
  StatusIconImage,
  SummarySection,
  TotalsGrid,
  EmptyState,
} from '../Css/StyledAsistencia.js';

const AUSENTE_CON_AVISO = 'ausente con aviso';

const ATTENDANCE_OPTIONS = [
  ESTADOS_CITA.ASISTIO,
  ESTADOS_CITA.NO_ASISTIO,
  AUSENTE_CON_AVISO,
  ESTADOS_CITA.PENDIENTE
];

const ATTENDANCE_LABELS = {
  [ESTADOS_CITA.ASISTIO]: 'presente',
  [ESTADOS_CITA.NO_ASISTIO]: 'ausente',
  [AUSENTE_CON_AVISO]: AUSENTE_CON_AVISO,
  [ESTADOS_CITA.PENDIENTE]: ESTADOS_CITA.PENDIENTE
};

const numberFormatter = new Intl.NumberFormat('es-AR');

const getAttendanceVariant = (status) => {
  if (!status) {
    return 'default';
  }

  return status
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '-');
};

const formatPaymentMethod = (method) => {
  if (!method) {
    return '—';
  }

  const normalized = method.toString().trim();
  if (!normalized) {
    return '—';
  }

  const lower = normalized.toLowerCase();
  const paymentLabels = {
    efectivo: 'Efectivo',
    transferencia: 'Transferencia',
    tarjeta: 'Tarjeta',
    cud: 'CUD',
  };

  return paymentLabels[lower] || normalized;
};

const formatInputDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const getDefaultDateRange = () => {
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  return {
    from: formatInputDate(firstDayOfMonth),
    to: formatInputDate(today),
  };
};

const parseRowDate = (dateString) => {
  if (!dateString) {
    return null;
  }

  if (dateString.includes('/')) {
    const [day, month, year] = dateString.split('/');
    if (!day || !month || !year) {
      return null;
    }
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  const parsed = new Date(`${dateString}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDisplayDate = (isoDate) => {
  const parsedDate = parseRowDate(isoDate);
  if (!parsedDate) {
    return '--';
  }

  const day = String(parsedDate.getDate()).padStart(2, '0');
  const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
  const year = parsedDate.getFullYear();

  return `${day}/${month}/${year}`;
};

const Asistencia = () => {
  const [fromDate, setFromDate] = useState(() => getDefaultDateRange().from);
  const [toDate, setToDate] = useState(() => getDefaultDateRange().to);
  const [searchTerm, setSearchTerm] = useState('');
  const [appointments, setAppointments] = useState(CITAS_MOCK);

  const filteredRows = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const from = fromDate ? new Date(`${fromDate}T00:00:00`) : null;
    const to = toDate ? new Date(`${toDate}T23:59:59`) : null;

    return appointments
      .filter((appointment) => {
        const appointmentDate = parseRowDate(appointment.fecha);
        const matchesSearch = normalizedSearch
          ? appointment.paciente_nombre.toLowerCase().includes(normalizedSearch)
          : true;

        const withinFrom = from ? (appointmentDate ? appointmentDate >= from : false) : true;
        const withinTo = to ? (appointmentDate ? appointmentDate <= to : false) : true;

        return matchesSearch && withinFrom && withinTo;
      })
      .sort((a, b) => {
        const dateA = parseRowDate(a.fecha)?.getTime() ?? 0;
        const dateB = parseRowDate(b.fecha)?.getTime() ?? 0;
        const dateDiff = dateA - dateB;

        if (dateDiff !== 0) {
          return dateDiff;
        }
        return a.hora_inicio.localeCompare(b.hora_inicio);
      });
  }, [appointments, fromDate, toDate, searchTerm]);

  const metrics = useMemo(() => {
    if (!filteredRows.length) {
      return {
        sessions: 0,
        patients: 0,
        totalBilled: 0,
        pendingPayment: 0,
      };
    }

    const sessions = filteredRows.length;

    const patientIds = new Set();
    filteredRows.forEach((row) => {
      if (row.paciente_id != null) {
        patientIds.add(row.paciente_id);
      }
    });

    const totalBilled = filteredRows.reduce((acc, row) => {
      if (row.abonado === ESTADOS_PAGO.PAGO) {
        return acc + (Number(row.valor_sesion) || 0);
      }
      return acc;
    }, 0);

    const pendingPayment = filteredRows.reduce((acc, row) => {
      if (row.abonado === ESTADOS_PAGO.NO_PAGO) {
        return acc + 15000;
      }
      return acc;
    }, 0);

    return {
      sessions,
      patients: patientIds.size,
      totalBilled,
      pendingPayment,
    };
  }, [filteredRows]);

  const handleAttendanceChange = (appointmentId, newStatus) => {
    setAppointments((prevAppointments) =>
      prevAppointments.map((appointment) =>
        appointment.id === appointmentId
          ? { ...appointment, estado_cita: newStatus }
          : appointment,
      ),
    );
  };

  return (
    <Cuerpo>
      <HeaderUno />
      <HeaderDos link="/escritorio/pacientes/paciente" componente="Asistencia y Cobros General" paciente />

      <AssistanceMainContainer>
        <AssistanceContent>
          <MetricsGrid>
            <MetricCard>
              <MetricLabel>CANTIDAD DE SESIONES</MetricLabel>
              <MetricValue>{numberFormatter.format(metrics.sessions)}</MetricValue>
            </MetricCard>
            <MetricCard>
              <MetricLabel>CANTIDAD DE PACIENTES</MetricLabel>
              <MetricValue>{numberFormatter.format(metrics.patients)}</MetricValue>
            </MetricCard>
          </MetricsGrid>

          <FiltersCard>
            <FiltersRow>
              <FiltersLabel>Filtrar</FiltersLabel>
              <FilterGroup>
                <FilterText>desde:</FilterText>
                <DateInput type="date" value={fromDate} onChange={(event) => setFromDate(event.target.value)} />
              </FilterGroup>

              <FilterGroup>
                <FilterText>hasta:</FilterText>
                <DateInput type="date" value={toDate} onChange={(event) => setToDate(event.target.value)} />
              </FilterGroup>

              <SearchWrapper>
                <SearchInput
                  type="text"
                  placeholder="Buscar paciente..."
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                />
                <IconButton type="button" aria-label="Buscar paciente">
                  <SearchIcon src={searchIcon} alt="Buscar" />
                </IconButton>
              </SearchWrapper>
            </FiltersRow>
          </FiltersCard>

          <TableCard>
            <TableHeaderRow>
              <TableHeaderCell>Fecha</TableHeaderCell>
              <TableHeaderCell>Paciente</TableHeaderCell>
              <TableHeaderCell>Asistencia</TableHeaderCell>
              <TableHeaderCell $center>Honorarios</TableHeaderCell>
              <TableHeaderCell>Forma de pago</TableHeaderCell>
              <TableHeaderCell $center>CUD</TableHeaderCell>
              <TableHeaderCell $center>Pago</TableHeaderCell>
            </TableHeaderRow>

            <TableScroll>
              {filteredRows.length > 0 ? (
                <TableBody>
                  {filteredRows.map((row) => {
                    const hasCud = Boolean(row.cud);
                    const hasPaid = row.abonado === ESTADOS_PAGO.PAGO;
                    const cudIconSrc = hasCud ? confirmedIcon : cancelledIcon;
                    const paymentIconSrc = hasPaid ? confirmedIcon : cancelledIcon;
                    const patientName = row.paciente_nombre || '';
                    const nameParts = patientName.trim().split(/\s+/);
                    const lastName = nameParts.pop() || '';
                    const firstNames = nameParts.join(' ');
                    const formattedPatientName = lastName
                      ? `${lastName.toUpperCase()}, ${firstNames}`.trim().replace(/,\s*$/, '')
                      : patientName;

                    return (
                      <TableRow key={row.id}>
                        <TableCell>{formatDisplayDate(row.fecha)}</TableCell>
                        <TableCell>{formattedPatientName}</TableCell>
                        <TableCell>
                          <AttendanceSelect
                            defaultValue={row.estado_cita}
                            data-attendance={getAttendanceVariant(row.estado_cita)}
                            onChange={(event) => handleAttendanceChange(row.id, event.target.value)}
                          >
                            {ATTENDANCE_OPTIONS.map((option) => (
                              <option key={option} value={option}>
                                {ATTENDANCE_LABELS[option] || option}
                              </option>
                            ))}
                          </AttendanceSelect>
                        </TableCell>
                        <TableCell $center>{`$ ${numberFormatter.format(row.valor_sesion ?? 0)}`}</TableCell>
                        <TableCell>{formatPaymentMethod(row.forma_pago)}</TableCell>
                        <TableCell $center>
                          <StatusIconWrapper>
                            <StatusIconImage
                              src={cudIconSrc}
                              alt={hasCud ? 'Paciente con CUD' : 'Paciente sin CUD'}
                            />
                          </StatusIconWrapper>
                        </TableCell>
                        <TableCell $center>
                          <StatusIconWrapper>
                            <StatusIconImage
                              src={paymentIconSrc}
                              alt={hasPaid ? 'Pago confirmado' : 'Pago pendiente'}
                            />
                          </StatusIconWrapper>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              ) : (
                <EmptyState>No se encontraron registros para los filtros aplicados.</EmptyState>
              )}
            </TableScroll>
          </TableCard>

          <SummarySection>
            <TotalsGrid>
              <MetricCard>
                <MetricLabel>TOTAL FACTURADO</MetricLabel>
                <MetricValue>{`$ ${numberFormatter.format(metrics.totalBilled)}`}</MetricValue>
              </MetricCard>

              <MetricCard>
                <MetricLabel>FALTA PAGAR</MetricLabel>
                <MetricValue>{`$ ${numberFormatter.format(metrics.pendingPayment)}`}</MetricValue>
              </MetricCard>
            </TotalsGrid>
          </SummarySection>
        </AssistanceContent>
      </AssistanceMainContainer>
    </Cuerpo>
  );
};

export default Asistencia;
